/**
 * @author Martin Leonov <martin.leonov@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */
/* eslint-disable max-statements */
/* eslint-disable no-await-in-loop */

import AssertionError from 'assertion-error';

import ErrorName from '../../error/error-name';
import promisifyTimeWatcher from '../../promisify/promisify-time-watcher';
import ViewerDelayerFactory from '../../viewer/delayer/viewer-delayer-factory';
import { BaseContentController } from './base-content-controller';
import { isLeafContentController } from './content-controller-util';
import { generatePlayErrorElem } from './content-error-overlay';
import { DeterministicPlaylistSimulator } from './playlist-simulator';

/**
 * @import { ContentController, ContentControllerFactory, ContentControllerType, Spot, SpotContent } from '.'
 * @import { Content } from '../content'
 * @import { Playlist } from '../content'
 * @import { ContentInfo } from '../../content/info/content-info'
 * @import { Logger } from '../../logger/logger'
 * @import UnixTimestamp from '../../unix-timestamp/unix-timestamp'
 * @import ViewerDelayer from '../../viewer/delayer/viewer-delayer'
 */

/**
 * @typedef {{spot: Spot | null; contentController: ContentController}} DeterministicContent
 */

/**
 * @implements {ContentController}
 */
export class PlaylistDeterministicContentController extends BaseContentController {
  /**
   * @param {ContentControllerFactory} contentControllerFactory
   * @param {ContentInfo} contentInfo
   * @param {Content} content
   * @param {Logger} logger
   * @param {HTMLElement} parentEl
   * @param {number} parentVolume
   * @param {UnixTimestamp} unixTimestamp
   */
  constructor(
    contentControllerFactory,
    contentInfo,
    content,
    logger,
    parentEl,
    parentVolume,
    unixTimestamp
  ) {
    super();
    this.contentControllerFactory = contentControllerFactory;
    this.contentInfo = contentInfo;
    this.content = content;
    this.logger = logger;
    this.parentEl = parentEl;
    this.parentVolume = parentVolume;
    this.unixTimestamp = unixTimestamp;

    /** @type {ContentControllerType} */
    this.type = 'playlistDeterministic';

    /** @type {Playlist | undefined} */
    const playlist = this.content.playlist;
    if (!playlist || !playlist.deterministic) {
      throw new Error('content should be deterministic playlist');
    }
    this.playlist = playlist;

    this.unixTimeOffset = unixTimestamp.offset();

    /** @type {DeterministicPlaylistSimulator} */
    this.simulator = this.createSimulator();

    /** @type {AbortController | undefined} */
    this.abortController = undefined;
    /** @type {ViewerDelayer} */
    this.delayer = new ViewerDelayerFactory({
      delay: 1000,
      limit: 5
    }).create();
    /** @type {DeterministicContent | undefined} */
    this.currentDeterministicContent = undefined;
    /** @type {DeterministicContent | undefined} */
    this.nextDeterministicContent = undefined;
    /** @type {(() => void) | undefined} */
    this.removePictogram = undefined;
    this.loopQueue = Promise.resolve();

    this.now = this.now.bind(this);
  }

  /**
   * @param {SpotContent} content
   * @param {number} duration
   * @param {number} volume
   * @returns {ContentController}
   */
  createContentController(content, duration, volume) {
    const { model } = content;
    return this.contentControllerFactory.create(this.parentEl, model, {
      contentInfo: this.contentInfo,
      volume,
      trackDuration: duration
    });
  }

  /**
   * @returns {DeterministicPlaylistSimulator}
   */
  createSimulator() {
    return new DeterministicPlaylistSimulator(
      this.playlist,
      this.parentVolume,
      this.unixTimestamp
    );
  }

  /** @type {ContentController['preload']} */
  async preload() {
    await super.preloadCallback(async () => {
      // TODO: V době preloadu nevíme, co by se mělo přednačíst. Možná předávat
      // preload offset (délka aktuálně přehrávaného media), a toto medium přednačíst.
      this.abortController = await new AbortController();
      this.loopQueue = Promise.resolve();
    });
  }

  /** @type {ContentController['restart']} */
  async restart() {
    // Není potřeba dělat nic, protože reset deterministického playlistu způsobí spuštění ve stejném místě,
    // kde playlist skončil.
  }

  /** @type {ContentController['start']} */
  async start() {
    const { abortController } = this;
    if (!abortController) {
      throw new AssertionError('AbortController not set');
    }

    // eslint-disable-next-line require-await
    await super.startCallback(async () => {
      // Spuštění asynchronně přehrávací smyčky.
      (async () => {
        while (!abortController.signal.aborted) {
          try {
            this.loopQueue = this.startDeterministic(abortController).then(
              (success) =>
                success ? this.delayer.succeed() : this.delayer.fail()
            );
            await this.loopQueue;
          } catch (err) {
            const error = new Error(
              `deterministic start ${/** @type {Error} */ (err).toString()}`
            );
            this.logger.error(error);
            await this.delayer.fail();
            this.loopQueue = Promise.resolve();
          }
        }
      })();
    });
  }

  /**
   * @param {AbortController} abortController
   * @returns {Promise<boolean>}
   */
  async startDeterministic(abortController) {
    this.unixTimeOffset = this.unixTimestamp.offset();
    // Uložení příznaku jestli je deterministický obsah preloadovaný z předchozího volání.
    const hasPreloadedDeterministicContent = Boolean(
      this.nextDeterministicContent
    );
    /** @type {number | undefined} */
    let time = this.now();

    if (this.nextDeterministicContent) {
      this.currentDeterministicContent = this.nextDeterministicContent;
      this.nextDeterministicContent = undefined;
    } else {
      const spot = await this.simulator.next();
      if (!spot || !spot.content) {
        return false;
      }
      const contentController = this.createContentController(
        spot.content,
        spot.duration / 1000,
        spot.volume
      );
      if (!contentController) {
        return false;
      }
      this.currentDeterministicContent = { contentController, spot };
    }

    const { contentController, spot } = this.currentDeterministicContent;
    if (!spot || !spot.content) {
      return false;
    }
    const durationUs = spot.time + spot.duration - time;
    const durationMs = durationUs / 1000;

    let played = false;
    const onError = () => (played = false);
    /** @type {(content: Content) => void} */
    const onPlayed = (content) => {
      if (isLeafContentController(contentController)) {
        return;
      }
      // Emitování played eventů pouze pro nekoncové ContentController.
      this.emitter.emit('played', content);
    };
    /** @type {(content: Content) => void} */
    const onPlaying = (content) => {
      if (isLeafContentController(contentController)) {
        return;
      }
      // Emitování playing eventů pouze pro nekoncové ContentController.
      this.emitter.emit('playing', content);
    };

    contentController.on('error', onError);
    contentController.on('played', onPlayed);
    contentController.on('playing', onPlaying);

    const abortSignal = abortController.signal;
    const timeWatcher = promisifyTimeWatcher(
      spot.time + spot.duration,
      this.now,
      { abortSignal }
      // Umyslné ignorování chyby. Protože nás nezajímá informace o přerušení.
    ).catch(() => {});
    try {
      // Kontrola jestli je doba přehrávání menší než zadaný interval.
      // Toto se kontroluje kvůli tomu, že na tak krátkou chvílí nemá cenu pouštět přehrávání.
      // TODO: jaký časový interval
      if (durationMs > 1000) {
        if (!hasPreloadedDeterministicContent) {
          await contentController.preload();
        }

        time = this.now();
        await contentController.start((time - spot.time) / 1000);
        this.emitter.emit('playing', spot.content.model);
        played = true;
      }
    } catch (err) {
      const errorEl = generatePlayErrorElem(
        /** @type {Error} */ (err),
        spot.content.model
      );
      this.parentEl.appendChild(errorEl);
      this.removePictogram = () => {
        this.parentEl.removeChild(errorEl);
        this.removePictogram = undefined;
      };
    }

    // Přeskočení preloadu v případech, pokud hraje např. deterministický playlist, šablona.
    // V těchto případech není známý obsah "uvnitř" a není žádoucí spotřebovat omezené zdroje (např. video element) na preload.
    if (isLeafContentController(contentController)) {
      // Přednačtení dalšího media v pořadí.
      /** @type {Spot | null | undefined} */
      let nextSpot;
      try {
        nextSpot = await this.simulator.peek(spot);
        if (nextSpot && nextSpot.content) {
          const nextContentController = this.createContentController(
            nextSpot.content,
            nextSpot.duration / 1000,
            nextSpot.volume
          );
          if (nextContentController) {
            await nextContentController.preload();

            // Musí se nastavit až po preload, protože to může neuspět.
            this.nextDeterministicContent = {
              spot: nextSpot,
              contentController: nextContentController
            };
          }
        }
      } catch (err) {
        // Preload se nepovedl, např. kvůli vyčerpání aktuálně vypůjčených zdrojů.
      }
    }

    await timeWatcher
      .catch((/** @type {Error} */ error) => {
        // Jiné, než AbortError, nejsou očekavané.
        if (error.name !== ErrorName.AbortError) {
          throw error;
        }
      })
      .finally(async () => {
        if (played && spot.content) {
          this.emitter.emit('played', spot.content.model);
        }

        this.removePictogram?.();

        contentController.off('error', onError);
        contentController.off('played', onPlayed);
        contentController.off('playing', onPlaying);
        // Umyslené ignorování chyby, které mohly vzniknout při zastavování.
        await contentController.stop().catch(() => {});
        this.currentDeterministicContent = undefined;
      });
    return true;
  }

  /** @type {ContentController['stop']} */
  async stop() {
    try {
      await super.stopCallback(async () => {
        this.abortController?.abort();
        // Prvně se musí počkat na dokončení přehrávací smyčky.
        await this.loopQueue.catch(() => {});

        this.delayer.destroy();
        await this.currentDeterministicContent?.contentController
          .stop()
          .catch(() => {});
        await this.nextDeterministicContent?.contentController
          .stop()
          .catch(() => {});

        this.currentDeterministicContent = undefined;
        this.nextDeterministicContent = undefined;
        this.loopQueue = Promise.resolve();
      });
    } catch {
      // Úmyslené ignorování chyby. Pokud chyba vznikla při stop,
      // tak aby metoda stop byla přerušena. Ten případ by neměl nastávat.
    }
  }

  /**
   * Zjistí aktuální čas.
   * @private
   * @returns {number} Aktuální čas v mikrosekundách.
   */
  now() {
    return (this.unixTimestamp.now() + this.unixTimeOffset) * 1000;
  }
}
